import { useEffect } from "react";
import { App, Card, Checkbox, Col, Form, Row, Spin, Typography, Table } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton/Button";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";
import CustomInput from "../../../components/CustomInput/Input";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import {
  clearBankOpsMessages,
  clearDisburseDealDetails,
  confirmManualDisburse,
  fetchDisburseDealDetails,
  fetchDisburseDeals,
} from "../../../redux/slices/bankOperationsSlice";
import { getUtrRules, UTR_MAX_LENGTH, getRemarksRules, REMARKS_MAX_LENGTH, toTransactionDateIso, formatCurrency } from "../../../helpers/helpers";
import { SHOW_MARK_AS_DUPLICATE } from "../../../constants/manualDuplicate";

const STATUS_OPTIONS = [
  { value: "SUCCESS", label: "Success" },
];
const ManualDisburseForm = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { disburseDeals, disburseDealDetails, submitLoading } = useSelector(
    (state) => state.bankOperations
  );

  useEffect(() => {
    dispatch(fetchDisburseDeals());
  }, [dispatch]);

  const onFinish = async (values) => {
    const { businessOrgId, enterpriseName, remarks, isDuplicate, ...payload } = values;
    const result = await dispatch(
      confirmManualDisburse({
        ...payload,
        remarks: remarks?.trim() || undefined,
        transactionDate: toTransactionDateIso(payload.transactionDate),
        isDuplicate: Boolean(isDuplicate),
      })
    );

    if (confirmManualDisburse.fulfilled.match(result)) {
      message.success(
        isDuplicate
          ? "Disbursement recorded as duplicate (excluded from wallet and ledger)"
          : "Disbursement confirmed successfully"
      );
      form.resetFields();
      dispatch(fetchDisburseDeals());
      dispatch(clearBankOpsMessages());
      return;
    }

    if (confirmManualDisburse.rejected.match(result)) {
      message.error(result.payload || "Failed to confirm disbursement", 5);
      dispatch(clearBankOpsMessages());
    }
  };

  const handleDealChange = async (dealId) => {
    dispatch(clearDisburseDealDetails());
    if (!dealId) return;
    const result = await dispatch(fetchDisburseDealDetails(dealId));
    const details = result.payload;
    if (details) {
      form.setFieldsValue({
        investorOrgId: details.investors?.[0]?.orgId,
        amount: details.suggestedDisbursedAmt,
        businessOrgId: details.business?.orgId,
        enterpriseName: details.enterprise?.name,
        status: "SUCCESS",
      });
    }
  };

  return (
    <Card title="Disbursement">
      <Spin spinning={submitLoading}>
        {disburseDealDetails?.supplierPayments?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>Supplier Payment Breakdown (SCF)</Typography.Title>
            <Table
              dataSource={disburseDealDetails.supplierPayments}
              rowKey="supplierId"
              pagination={false}
              size="small"
              bordered
              columns={[
                {
                  title: 'Supplier Name',
                  dataIndex: 'supplierName',
                  key: 'supplierName',
                },
                {
                  title: 'Payment Amount',
                  dataIndex: 'paymentAmount',
                  key: 'paymentAmount',
                  render: (val) => formatCurrency(val)
                }
              ]}
            />
          </div>
        )}
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ status: "SUCCESS", isDuplicate: false }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Deal ID"
                name="dealId"
                isRequired
                rules={[{ required: true, message: "Select a deal" }]}
                showSearch
                placeholder="Pending disbursement deals only"
                onChange={handleDealChange}
                options={(disburseDeals || []).map((deal) => ({
                  value: deal.dealId,
                  label: `${deal.dealId} - ${deal.orgName || deal.businessName || ""}`,
                }))}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Investor"
                name="investorOrgId"
                isRequired
                rules={[{ required: true, message: "Select investor" }]}
                showSearch
                placeholder="Investor on deal"
                options={(disburseDealDetails?.investors || []).map((investor) => ({
                  value: investor.orgId,
                  label: `${investor.name} (${investor.amount})`,
                }))}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Business"
                name="businessOrgId"
                disabled
                options={
                  disburseDealDetails?.business
                    ? [
                        {
                          value: disburseDealDetails.business.orgId,
                          label: disburseDealDetails.business.name,
                        },
                      ]
                    : []
                }
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Enterprise"
                name="enterpriseName"
                disabled
                placeholder="Auto-filled from deal"
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInputNumber
                label="Transaction amount"
                name="amount"
                isRequired
                rules={[{ required: true, message: "Amount is required" }]}
                min={1}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Bank UTR"
                name="utr"
                isRequired
                normalize={(value) => value?.replace(/\s/g, "")}
                maxLength={UTR_MAX_LENGTH}
                rules={getUtrRules({ label: "Bank UTR" })}
                placeholder="UTR from the bank"
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomDatePicker
                label="Transaction date"
                name="transactionDate"
                isRequired
                placeholder="Select transaction date"
                disabledDate={(current) => current && current > dayjs().endOf("day")}
                rules={[
                  { required: true, message: "Transaction date is required" },
                  {
                    validator: (_, value) => {
                      if (!value || !value.isAfter(dayjs(), "day")) return Promise.resolve();
                      return Promise.reject(new Error("Future dates are not allowed"));
                    },
                  },
                ]}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Status"
                name="status"
                isRequired
                rules={[{ required: true }]}
                options={STATUS_OPTIONS}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Bank reference"
                name="bankReference"
                placeholder="Optional reference number"
              />
            </Col>
            <Col xs={24}>
              <CustomInput
                label="Remarks"
                name="remarks"
                isTextArea
                rows={2}
                maxLength={REMARKS_MAX_LENGTH}
                showCount
                rules={getRemarksRules()}
              />
            </Col>
            {SHOW_MARK_AS_DUPLICATE && (
              <Col xs={24}>
                <Form.Item name="isDuplicate" valuePropName="checked" style={{ marginBottom: 8 }}>
                  <Checkbox>
                    Mark as duplicate (already posted on another path — do not update wallet or ledger)
                  </Checkbox>
                </Form.Item>
              </Col>
            )}
          </Row>
          <CustomButton type="primary" htmlType="submit" loading={submitLoading}>
            Confirm disbursement
          </CustomButton>
        </Form>
      </Spin>
    </Card>
  );
};

export default ManualDisburseForm;
